import React, { useState } from 'react';
import { Download, Smartphone, Share, CheckCircle, X } from 'lucide-react';
import usePWAInstall from '../hooks/usePWAInstall';

/**
 * Botón/Card de instalación de PWA
 * Se muestra solo cuando la app es instalable y no está instalada
 */
const InstallAppButton = ({ variant = 'card' }) => {
  const { isInstallable, isInstalled, isIOS, canPrompt, installApp } = usePWAInstall();
  const [showIOSModal, setShowIOSModal] = useState(false);
  const [installing, setInstalling] = useState(false);

  // No mostrar si ya está instalada o no es instalable
  if (isInstalled || !isInstallable) {
    return null;
  }

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSModal(true);
      return;
    }

    if (canPrompt) {
      setInstalling(true);
      const result = await installApp();
      setInstalling(false);
      
      if (result.success) {
        // Mostrar mensaje de éxito opcional
      }
    }
  };

  // Variante: Botón compacto para header
  if (variant === 'button') {
    return (
      <>
        <button
          onClick={handleInstall}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#C49F27] text-black text-[11px] uppercase tracking-wider font-semibold rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-200"
          data-testid="install-app-btn"
        >
          <Download className="w-4 h-4" />
          <span className="hidden sm:inline">Instalar App</span>
        </button>
        
        {showIOSModal && (
          <IOSInstallModal onClose={() => setShowIOSModal(false)} />
        )}
      </>
    );
  }

  // Variante: Card para Dashboard
  return (
    <>
      <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 rounded-2xl p-5 border border-zinc-700/50 shadow-xl mb-8">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#B8960F] flex items-center justify-center flex-shrink-0 shadow-lg">
            <Smartphone className="w-7 h-7 text-black" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-serif text-lg mb-1">
              Instala ATELIER JPM
            </h3>
            <p className="text-zinc-400 text-sm leading-relaxed mb-4">
              Accede más rápido desde tu pantalla de inicio. 
              Funciona como una app nativa en tu {isIOS ? 'iPhone/iPad' : 'dispositivo'}.
            </p>
            
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleInstall}
                disabled={installing}
                className="flex items-center gap-2 bg-white text-black hover:bg-zinc-100 px-5 py-2.5 rounded-lg font-medium text-sm transition-all hover:shadow-md disabled:opacity-50"
                data-testid="install-app-card-btn"
              >
                {installing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Instalando...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    {isIOS ? 'Cómo instalar' : 'Instalar ahora'}
                  </>
                )}
              </button>
              
              <div className="flex items-center gap-4 text-xs text-zinc-500">
                <span className="flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                  Gratis
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                  Sin App Store
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                  Acceso offline
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {showIOSModal && (
        <IOSInstallModal onClose={() => setShowIOSModal(false)} />
      )}
    </>
  );
};

// Modal con instrucciones para iOS
const IOSInstallModal = ({ onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl animate-in zoom-in-95 duration-200">
        <div className="p-5 border-b border-zinc-100">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-xl text-black">Instalar en iOS</h3>
            <button 
              onClick={onClose}
              className="p-1.5 hover:bg-zinc-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-zinc-400" />
            </button>
          </div>
        </div>
        
        <div className="p-5 space-y-5">
          {/* Paso 1 */}
          <div className="flex gap-4">
            <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 text-white font-bold">
              1
            </div>
            <div>
              <p className="font-medium text-black mb-1">Toca el botón Compartir</p>
              <p className="text-sm text-zinc-500">
                En Safari, toca el ícono <Share className="w-4 h-4 inline text-blue-500" /> en la barra inferior
              </p>
            </div>
          </div>
          
          {/* Paso 2 */}
          <div className="flex gap-4">
            <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 text-white font-bold">
              2
            </div>
            <div>
              <p className="font-medium text-black mb-1">Selecciona &quot;Agregar a inicio&quot;</p>
              <p className="text-sm text-zinc-500">
                Desplázate en el menú y busca la opción con el ícono ➕
              </p>
            </div>
          </div>
          
          {/* Paso 3 */}
          <div className="flex gap-4">
            <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 text-white font-bold">
              3
            </div>
            <div>
              <p className="font-medium text-black mb-1">Confirma la instalación</p>
              <p className="text-sm text-zinc-500">
                Toca &quot;Agregar&quot; y la app aparecerá en tu pantalla de inicio
              </p>
            </div>
          </div>
        </div>
        
        <div className="p-5 bg-zinc-50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="w-full py-3 bg-black text-white rounded-xl font-medium hover:bg-zinc-800 transition-colors"
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallAppButton;
